import { useMergeRefs } from '@floating-ui/react'
import { css, cx } from '@linaria/core'
import { type ComponentProps, type ElementType, type FC, type ReactNode, type Ref, use, useCallback, useEffect, useId, useRef } from 'react'
import { ReactReduxContext } from 'react-redux'
import { useSharedAppSelector } from '#store/sharedBaseStore'
import { fragments, style } from '#styles'
import { useWindowZoom } from '#utils/useWindowZoom'

type OffsetValue =
  | number
  | {
      /**
       * The axis that runs along the side of the floating element. Represents
       * the distance (gutter or margin) between the reference and floating
       * element.
       * @default 0
       */
      mainAxis?: number
      /**
       * The axis that runs along the alignment of the floating element.
       * Represents the skidding between the reference and floating element.
       * @default 0
       */
      crossAxis?: number
    }

type TooltipProps<T extends keyof HTMLElementTagNameMap> = Omit<ComponentProps<T>, 'title' | 'disabled'> & {
  disabled?: boolean
  immediate?: boolean
  children?: ReactNode
  className?: string
  shortTitle?: string
  title?: ReactNode
  appearance?: 'shadow' | 'blurred' | 'flat' | 'transparent'
  offset?: OffsetValue
  renderComponent?: T
  forceOpen?: boolean
  allowTooltipWhenDisabled?: boolean
}

const popoverStyle = css`
  pointer-events: none;
  padding: 3px 10px;
  border-radius: 2px;
  position: absolute;
  
  border: none;
  overflow: visible;
  transition: 0.15s ease opacity var(--tooltip-transition-delay);
  transition-delay:  var(--tooltip-transition-delay);


  & ._immediate-visible {
    transition-delay: 0;
  }
  
  --translate-x: calc(-50% - var(--offsetCross, 0));
  --translate-y: calc(0.5 * var(--height, 0));
  
  &.vertical-anchor-bottom {
    --translate-y: var(--height, 0);
  }
  
  &.vertical-anchor-top {
    --translate-y: -100%;
  }
  
  &.horizontal-anchor-left {
    --translate-x: calc(-100% - var(--offsetCross, 0) + var(--width, 0) / 2);
  }
  
  &.horizontal-anchor-right {
    --translate-x: calc(var(--offsetCross, 0) - var(--width, 0) / 2);
  }
  
  transform: translate(calc(var(--translate-x, 0)), calc(var(--translate-y, 0)));
  top: calc(var(--top, 0) + var(--offset, 0));
  left: calc(var(--left, 0) + var(--width, 0) / 2);
  min-width: var(--width, 0);

  @starting-style {
    opacity: 0;
  }

  > ._content {
    max-width: 300px;
    white-space: pre-wrap;
  }

  &.appearance-shadow,
  &.appearance-flat {
    color: ${style.themeColors.backgroundDefault};
    background: ${style.themeColors.textDefault};
    ${fragments.textStyle.body.m.regular}
  }

  &.appearance-shadow {
    filter: drop-shadow(0px 4px 16px rgba(0, 0, 0, 0.25));
  }

  &.appearance-transparent {
    background: none;
    color: inherit;
    padding: 0;
  }

  &.appearance-blurred {
    background: none;
    backdrop-filter: blur(2px);
  }
`

function TooltipRef<T extends keyof HTMLElementTagNameMap>(props: TooltipProps<T>, ref?: Ref<HTMLElementTagNameMap[T]>) {
  const hasRedux = use(ReactReduxContext)
  if (!hasRedux) {
    return undefined
  }
  return TooltipRefInner(props, ref)
}

// this is terribly annoying global state keeping to set a property consistently across all hover tooltips
const globalHoverHandler = (() => {
  // how long it takes after hovering to engage the short transition delay
  const engagementDelay = 1000
  // how long it takes after no element is hovered to disengage the short transition timing
  const disengagementDelay = 1000

  let timeoutHandle: ReturnType<typeof setTimeout> | undefined

  const onHover = () => {
    if (timeoutHandle) {
      clearTimeout(timeoutHandle)
    }

    timeoutHandle = setTimeout(() => {
      document.body.classList.add('hover-quick-open')
      timeoutHandle = undefined
    }, engagementDelay)
  }

  const onHoverEnd = () => {
    if (timeoutHandle) {
      clearTimeout(timeoutHandle)
    }

    timeoutHandle = setTimeout(() => {
      document.body.classList.remove('hover-quick-open')
      timeoutHandle = undefined
    }, disengagementDelay)
  }

  return {
    onHover,
    onHoverEnd,
  }
})()

function TooltipRefInner<T extends keyof HTMLElementTagNameMap>(
  {
    children,
    appearance = 'shadow',
    offset: offsetOptions,
    renderComponent,
    shortTitle,
    title,
    forceOpen,
    allowTooltipWhenDisabled,
    className,
    disabled,
    immediate,
    ...additionalProps
  }: TooltipProps<T>,
  ref?: Ref<HTMLElementTagNameMap[T]>,
) {
  const forceHoverPopupsOpen = useSharedAppSelector((x) => x.componentConfiguration.forceHoverPopupsOpen)

  const tagName = renderComponent ?? 'div'
  const RenderComponent = tagName as ElementType<{ children: ReactNode }>

  const forceActive = forceHoverPopupsOpen || forceOpen
  const id = useId()
  const hasTitle = !!(shortTitle ?? title)

  const classNames = cx(className, forceActive && '_force-active', immediate && '_immediate-visible')
  const elementRef = useRef<HTMLElementTagNameMap[T]>(null)
  const combinedRef = useMergeRefs([ref, elementRef])

  const typedProps = {
    ...additionalProps,
    'aria-description': (shortTitle ?? typeof title === 'string') ? title : undefined,
    className: classNames,
    ref: combinedRef,
    popoverTarget: id,
  } as object

  const popover = useRef<HTMLDivElement>(null)
  const { inverse: inverseZoom } = useWindowZoom()

  const applyPopoverPosition = useCallback(() => {
    const element = elementRef.current as HTMLElement
    const rect = element.getBoundingClientRect()
    if (popover.current) {
      const mainAxis = (typeof offsetOptions === 'object' ? offsetOptions.mainAxis : offsetOptions) ?? 0
      const crossAxis = (typeof offsetOptions === 'object' ? offsetOptions.crossAxis : 0) ?? 0

      let verticalAnchor = 'bottom'
      if (rect.bottom > window.innerHeight - 50) {
        verticalAnchor = 'top'
      }

      let horizontalAnchor = 'center'
      if (rect.left < 50) {
        horizontalAnchor = 'right'
      }
      if (rect.right > window.innerWidth - 50) {
        horizontalAnchor = 'left'
      }

      popover.current.classList.remove('vertical-anchor-top', 'vertical-anchor-bottom')
      popover.current.classList.add(`vertical-anchor-${verticalAnchor}`)
      popover.current.classList.remove('horizontal-anchor-left', 'horizontal-anchor-right', 'horizontal-anchor-center')
      popover.current.classList.add(`horizontal-anchor-${horizontalAnchor}`)

      popover.current.style.setProperty('--top', `${rect.top * inverseZoom}px`)
      popover.current.style.setProperty('--left', `${rect.left * inverseZoom}px`)
      popover.current.style.setProperty('--width', `${rect.width * inverseZoom}px`)
      popover.current.style.setProperty('--height', `${rect.height * inverseZoom}px`)
      popover.current.style.setProperty('--offset', `${mainAxis}px`)
      popover.current.style.setProperty('--offsetCross', `${crossAxis}px`)
    }
  }, [offsetOptions, inverseZoom])

  useEffect(() => {
    if (disabled && !allowTooltipWhenDisabled) {
      popover.current?.hidePopover()
    }
  }, [disabled, allowTooltipWhenDisabled])

  const onPointerEnter = useCallback(() => {
    popover.current?.showPopover()
    globalHoverHandler.onHover()
    applyPopoverPosition()
  }, [applyPopoverPosition])

  const onPointerLeave = useCallback(() => {
    if (!forceActive) {
      popover.current?.hidePopover()
      globalHoverHandler.onHoverEnd()
    }
  }, [forceActive])

  useEffect(() => {
    if (forceActive) {
      applyPopoverPosition()
      popover.current?.showPopover()
      return () => {
        popover.current?.hidePopover()
      }
    }
  }, [forceActive, applyPopoverPosition])

  const onMouseUp = useCallback(
    (evt: React.MouseEvent<unknown>) => {
      if (!forceActive) {
        requestAnimationFrame(() => {
          let element = document.elementFromPoint(evt.pageX, evt.pageY)
          while (element) {
            if (element === elementRef.current) {
              return
            }
            if (element === document.body) {
              break
            }
            element = element.parentElement
          }
          popover.current?.hidePopover()
        })
      }
    },
    [forceActive],
  )

  return (
    <RenderComponent
      {...typedProps}
      disabled={disabled}
      onPointerEnter={disabled && !allowTooltipWhenDisabled ? undefined : hasTitle ? onPointerEnter : undefined}
      onPointerLeave={disabled && !allowTooltipWhenDisabled ? undefined : hasTitle ? onPointerLeave : undefined}
      onMouseUp={onMouseUp}
    >
      {children}
      <div
        // popover is defined, but experimental 'hint' is not yet allowed
        popover={forceActive ? 'manual' : ('hint' as 'auto')}
        id={id}
        ref={popover}
        className={cx(popoverStyle, `appearance-${appearance}`)}
      >
        <div className={cx('_content', `appearance-${appearance}`)}>{title ?? shortTitle}</div>
      </div>
    </RenderComponent>
  )
}

function makeTypedTooltip<T extends keyof HTMLElementTagNameMap>(renderComponent: T) {
  const wrapped: FC<Omit<TooltipProps<T>, 'renderComponent'> & { ref?: Ref<HTMLElementTagNameMap[T]> }> = ({ ref, ...props }) => {
    const combinedProps = { ...props, renderComponent: renderComponent } as unknown as TooltipProps<T>
    return TooltipRef(combinedProps, ref)
  }
  wrapped.displayName = `${renderComponent}(debug(shared(HoverTooltip)))`
  return wrapped
}

export const HoverTooltip = {
  div: makeTypedTooltip('div'),
  span: makeTypedTooltip('span'),
  button: makeTypedTooltip('button'),
  header: makeTypedTooltip('header'),
  label: makeTypedTooltip('label'),
  figure: makeTypedTooltip('figure'),
} as const
